import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import { useGLTF, useProgress } from '@react-three/drei';
import AppLoader from './loader';
import { fakeSoundPlayer, ISoundPlayer, SoundPlayer } from './SoundPlayer';

enum Stages {
  INIT,
  MODELS,
  IMAGES,
  DONE,
  ERROR
}

type ErrorStage = {
  current: Stages.ERROR;
  reason: string;
  stage: Exclude<Stages, Stages.ERROR | Stages.DONE>;
};

type NormalStage = {
  current: Exclude<Stages, Stages.ERROR>;
};

type Stage = ErrorStage | NormalStage;

interface IAssets {
  setAppLoading: (value: boolean) => void;
  sound: ISoundPlayer;
}

const AssetsContext = createContext<IAssets>({
  setAppLoading: () => {},
  sound: fakeSoundPlayer
});

interface Props {
  children: React.ReactNode;
}

const soundPlayer = new SoundPlayer();

export const AssetsContextProvider = ({ children }: Props) => {
  const player = useRef<SoundPlayer>(soundPlayer);
  const [stage, setStage] = useState<Stage>({ current: Stages.INIT });
  const [override, setOverride] = useState(true);
  const { active: gltfActive, errors: gltfErrors } = useProgress();

  useEffect(() => {
    loadModels();
    const root = document.getElementById('root');
    if (root) root.style.opacity = '1';
  }, []);

  const loadModels = () => {
    setStage({ current: Stages.MODELS });
    useGLTF.preload(models);
  };

  useEffect(() => {
    if (stage.current === Stages.MODELS && !gltfActive) {
      if (gltfErrors.length === 0) {
        loadImages();
      } else {
        setStage({
          current: Stages.ERROR,
          reason: gltfErrors[0],
          stage: Stages.MODELS
        });
      }
    }
  }, [stage, gltfActive, gltfErrors]);

  const loadImages = async () => {
    setStage({ current: Stages.IMAGES });
    const result = await Promise.all<{ status: boolean; src: string }>(
      images.map(
        (src) =>
          new Promise((resolve) => {
            const img = new Image();
            img.src = src;
            img.onload = () => resolve({ status: true, src });
            img.onerror = () => resolve({ status: false, src });
          })
      )
    );
    /*const errored = result.find((r) => !r.status);
    if (errored) {
      setStage({
        current: Stages.ERROR,
        reason: errored.src,
        stage: Stages.IMAGES
      });
    } else {
      setStage({ current: Stages.DONE });
    }*/
    setStage({ current: Stages.DONE });
  };

  const loaderVisible = stage.current !== Stages.DONE || override;

  return (
    <AssetsContext.Provider
      value={{
        setAppLoading: (value: boolean) => setOverride(value),
        sound: player.current
      }}
    >
      {stage.current === Stages.DONE && children}
      <AppLoader
        visible={loaderVisible}
        error={stage.current === Stages.ERROR}
      />
    </AssetsContext.Provider>
  );
};

const models = ['/assets/models/hands.glb', '/assets/models/hands_clone.glb'];

const images = [
  '/assets/images/common/cream_can.png',
  '/assets/images/common/metamask.png',
  '/assets/images/creamShop/cat.png',
  '/assets/images/fightResults/medal.png',
  '/assets/images/fightResults/safe.png',
  '/assets/images/header/pseudoShadow.svg',
  '/assets/images/header/pseudoShadowInverted.svg',
  '/assets/images/landing/cream_page/cat.png',
  '/assets/images/landing/cream_page/rays.png',
  '/assets/images/landing/cream_page/rays_mobile.png',
  '/assets/images/landing/main_page/hands.png',
  '/assets/images/landing/mint_page/hand_loading.png',
  '/assets/images/landing/pre_footer/roadmap/gamepad.png',
  '/assets/images/landing/pre_footer/roadmap/ghost.png',
  '/assets/images/landing/pre_footer/roadmap/hands.png',
  '/assets/images/landing/pre_footer/roadmap/ok.png',
  '/assets/images/landing/pre_footer/roadmap/robot.png',
  '/assets/images/landing/pre_footer/team/avatar1.png',
  '/assets/images/landing/pre_footer/team/avatar2.png',
  '/assets/images/landing/pre_footer/team/avatar3.png',
  '/assets/images/landing/pre_footer/team/avatar4.png',
  '/assets/images/landing/pre_footer/team/avatar5.png',
  '/assets/images/market/bg_hands.png',
  '/assets/images/pick/arc.png'
];

export const useAssets = () => useContext(AssetsContext);
